import { fetchJurisdiction } from '@rushplay/api-client';
import { path, merge, pathOr, keys, values, includes, not, isEmpty } from 'ramda';
import { SESSION_EXPIRED } from '@rushplay/websockets';

/**
 * @constant
 * @memberof rushplay/compliance/jurisdiction
 */
var SAVED = 'compliance/jurisdiction/SAVED';
/**
 * @constant
 * @memberof rushplay/compliance/jurisdiction
 */

var CLEARED = 'compliance/jurisdiction/CLEARED';

/**
 * Fetches and stores juristictions response to state
 * @memberof rushplay/compliance/jurisdiction
 * @returns {Object} Redux action
 */

function fetch() {
  return fetchJurisdiction({
    success: function success(res) {
      return save(res.value);
    },
    failure: function failure() {
      return save(new Error('jurisdiction.errors.failed-loading'));
    },
    version: 1
  });
}
/**
 * Stores jurisdiction data
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} jurisdiction object
 * @returns {Object} Redux action
 */

function save(config) {
  if (config instanceof Error) {
    return {
      type: SAVED,
      error: true,
      payload: config
    };
  }

  return {
    type: SAVED,
    payload: {
      licenseId: config.licenseId || '',
      links: config.links || [],
      moneyLimits: config.moneyLimits || [],
      realityCheck: config.realityCheck || {},
      selfExclusion: config.selfExclusion || {},
      sessionLimits: config.sessionLimits || [],
      showCurrentSessionTime: config.showCurrentSessionTime || false,
      showMoneyTransactionTotals: config.showMoneyTransactionTotals || false,
      signUp: config.signUp || {},
      timeOut: config.timeOut || {}
    }
  };
}
/**
 * Clears jurisdiction data
 * @memberof rushplay/compliance/jurisdiction
 * @returns {Object} Redux action
 */

function clear(payload) {
  return {
    type: CLEARED,
    error: payload instanceof Error,
    payload: payload
  };
}

function limitsIndex(array) {
  var result = {};
  var _iteratorNormalCompletion = true;
  var _didIteratorError = false;
  var _iteratorError = undefined;

  try {
    for (var _iterator = array[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
      var item = _step.value;
      result[item.type] = result[item.type] || {};
      result[item.type][item.period] = array.indexOf(item);
    }
  } catch (err) {
    _didIteratorError = true;
    _iteratorError = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion && _iterator.return != null) {
        _iterator.return();
      }
    } finally {
      if (_didIteratorError) {
        throw _iteratorError;
      }
    }
  }

  return result;
}

function reducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;

  if (path(['error'], action)) {
    return state;
  }

  switch (path(['type'], action)) {
    case SAVED:
      {
        return merge(action.payload, {
          moneyLimitsIndex: limitsIndex(action.payload.moneyLimits),
          sessionLimitsIndex: limitsIndex(action.payload.sessionLimits)
        });
      }

    case SESSION_EXPIRED:
    case CLEARED:
      {
        return {};
      }

    default:
      {
        return state;
      }
  }
}

var MONEY_LIMITS_FALLBACK = [];
var SESSION_LIMITS_FALLBACK = [];
var REALITY_CHECK_FALLBACK = {};
var REALITY_CHECK_VALUES_FALLBACK = [];
var SELF_EXCLUSION_FALLBACK = {};
var SELF_EXCLUSION_VALUES_FALLBACK = [];
var TIME_OUT_FALLBACK = {};
var TIME_OUT_VALUES_FALLBACK = [];
/**
 * @typedef {Object} MoneyLimit
 * @property {string} type Unique limit type (e.g. 'deposit')
 * @property {string} period Limit period (subtype, e.g. 'day')
 * @property {number} cooldown Time for the increase to take effect (in ms)
 * @property {boolean} crossbrand Set to `true` if setting the limit updates
 * other brands’ limit; `false` otherwise
 * @property {number} [softCap] Value considered as “too high limit” (in cents)
 */

/**
 * Get array of available money limits
 * @private
 * @param {Object} state Jurisdiction state
 * @returns {Array.<MoneyLimit>}
 */

function __moneyLimits(state) {
  return pathOr(MONEY_LIMITS_FALLBACK, ['moneyLimits'], state);
}
/**
 * @typedef {Object} SessionLimit
 * @property {string} period Limit period (subtype, e.g. 'day')
 * @property {number} cooldown Time for the increase to take effect (in ms)
 * @property {boolean} crossbrand Set to `true` if setting the limit updates
 * other brands’ limit; `false` otherwise
 */

/**
 * Get array of available session limits
 * @private
 * @param {Object} state Jurisdiction state
 * @returns {Array.<SessionLimit>}
 */


function __sessionLimits(state) {
  return pathOr(SESSION_LIMITS_FALLBACK, ['sessionLimits'], state);
}
/**
 * Get reality check config
 * @private
 * @param {Object} state Jurisdiction state
 * @param {Object} props
 */


function __realityCheck(state) {
  return pathOr(REALITY_CHECK_FALLBACK, ['realityCheck'], state);
}
/**
 * Get self exclusion config
 * @private
 * @param {Object} state Jurisdiction state
 * @param {Object} props
 */


function __selfExclusion(state) {
  return pathOr(SELF_EXCLUSION_FALLBACK, ['selfExclusion'], state);
}
/**
 * Get time out config
 * @private
 * @param {Object} state Jurisdiction state
 * @param {Object} props
 */


function __timeOut(state) {
  return pathOr(TIME_OUT_FALLBACK, ['timeOut'], state);
}
/**
 * Get if Self Exclusion is crossbrand
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @returns {boolean}
 */


function isSelfExclusionCrossbrand(state) {
  return pathOr(false, ['crossbrand'], __selfExclusion(state));
}
/**
 * Get if Time Out is crossbrand
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @returns {boolean}
 */

function isTimeOutCrossbrand(state) {
  return pathOr(false, ['crossbrand'], __timeOut(state));
}
/**
 * Get if total balance transactions from last 12 months should be visible
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @returns {boolean}
 */

function isBalanceTotalsVisible(state) {
  return pathOr(false, ['showMoneyTransactionTotals'], state);
}
/**
 * Get if current session time should be visible
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @returns {boolean}
 */

function getCurrentSessionTimeVisibility(state) {
  return pathOr(false, ['showCurrentSessionTime'], state);
}
/**
 * Get if we're allowing brite signups
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @returns {boolean}
 */

function getBriteSignUpAllowed(state) {
  return pathOr(false, ['signUp', 'brite'], state);
}
/**
 * Get if we're allowing brite signups via piq kyc
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @returns {boolean}
 */

function getBriteKycSignUpAllowed(state) {
  return pathOr(false, ['signUp', 'briteKyc'], state);
}
/**
 * Get Jurisdiction license
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @returns {string}
 */

function getLicense(state) {
  return pathOr('', ['licenseId'], state);
}
/**
 * Get Jurisdiction links
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @returns {Array}
 */

function getLinks(state) {
  return pathOr([], ['links'], state);
}
/**
 * Get if we're allowing manual signups
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @returns {boolean}
 */

function getManualSignUpAllowed(state) {
  return pathOr(false, ['signUp', 'manual'], state);
}
/**
 * Get array of available money limit types
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @returns {Array.<string>}
 */

function getMoneyLimitTypes(state) {
  return keys(state.moneyLimitsIndex);
}
/**
 * Get array of available money limit periods for given type
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @param {Object} props
 * @param {string} props.type Money limit type
 * @returns {Array.<string>}
 */

function getMoneyLimitPeriods(state, props) {
  return keys(pathOr({}, [props.type], state.moneyLimitsIndex));
}
/**
 * Get array of money limits IDs of given type
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @param {Object} props
 * @param {string} props.type Money limit type
 */

function getMoneyLimits(state, props) {
  return values(pathOr({}, [props.type], state.moneyLimitsIndex));
}
/**
 * Get money limits by ID
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @param {Object} props
 * @param {string} props.id Money limit ID
 * @param {string} props.period Money limit period (ignored if ID is passed)
 * @param {string} props.type Money limit type (ignored if ID is passed)
 */

function getMoneyLimit(state, props) {
  if (typeof props.id !== 'undefined') {
    return path([props.id], __moneyLimits(state));
  }

  var id = pathOr(-1, [props.type, props.period], state.moneyLimitsIndex);
  return getMoneyLimit(state, {
    id: id
  });
}
/**
 * Get array of available session limit types
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @returns {Array.<string>}
 */

function getSessionLimitTypes(state) {
  return keys(state.sessionLimitsIndex);
}
/**
 * Get array of available session limit periods for given type
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @param {Object} props
 * @param {string} props.type Session limit type
 * @returns {Array.<string>}
 */

function getSessionLimitPeriods(state, props) {
  return keys(pathOr({}, [props.type], state.sessionLimitsIndex));
}
/**
 * Get array of session limits IDs of given type
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @param {Object} props
 * @param {string} props.type Session limit type
 */

function getSessionLimits(state, props) {
  return values(pathOr({}, [props.type], state.sessionLimitsIndex));
}
/**
 * Get session limit by ID
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @param {Object} props
 * @param {string} props.id Session limit ID
 * @param {string} props.period Session limit period (ignored if ID is passed)
 * @param {string} props.type Session limit type (ignored if ID is passed)
 */

function getSessionLimit(state, props) {
  if (typeof props.id !== 'undefined') {
    return path([props.id], __sessionLimits(state));
  }

  var id = pathOr(-1, [props.type, props.period], state.sessionLimitsIndex);
  return getSessionLimit(state, {
    id: id
  });
}
/**
 * Get Reality Check configurability
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @returns {boolean}
 */

function getRealityCheckConfigurability(state) {
  return pathOr(false, ['configurable'], __realityCheck(state));
}
/**
 * Get Reality Check removability
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @returns {boolean}
 */

function getRealityCheckRemovability(state) {
  return pathOr(false, ['removable'], __realityCheck(state));
}
/**
 * Get Reality Check timer reset trigger
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @returns {("game-end"|"session-end")}
 */

function getRealityCheckTimerResetTrigger(state) {
  return pathOr('session-end', ['timerResetTrigger'], __realityCheck(state));
}
/**
 * Get Reality Check available values
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @returns {Array.<string>}
 */

function getRealityCheckValues(state) {
  return pathOr(REALITY_CHECK_VALUES_FALLBACK, ['values'], __realityCheck(state));
}
/**
 * Get Self Exclusion available values
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @returns {Array.<string>}
 */

function getSelfExclusionValues(state) {
  return pathOr(SELF_EXCLUSION_VALUES_FALLBACK, ['values'], __selfExclusion(state));
}
/**
 * Get Time Out available values
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @returns {Array.<string>}
 */

function getTimeOutValues(state) {
  return pathOr(TIME_OUT_VALUES_FALLBACK, ['values'], __timeOut(state));
}
/**
 * Get if we're allowing trustly signups
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @returns {boolean}
 */

function getTrustlySignUpAllowed(state) {
  return pathOr(false, ['signUp', 'trustly'], state);
}
/**
 * @typedef {Object.<string, boolean>} SettingsAvailability
 * @property {boolean} betLimits
 * @property {boolean} depositLimits
 * @property {boolean} loginTimeLimits
 * @property {boolean} lossLimits
 * @property {boolean} realityCheck
 * @property {boolean} selfExclusion
 * @property {boolean} timeOut
 */

/**
 * Get responsible gambling tools availability
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @returns {SettingsAvailability}
 */

function getSettingsAvailability(state) {
  var moneyLimits = getMoneyLimitTypes(state);
  var sessionLimits = getSessionLimitTypes(state);
  return {
    betLimits: includes('bet', moneyLimits),
    depositLimits: includes('deposit', moneyLimits),
    loginTimeLimits: includes('loginTime', sessionLimits),
    lossLimits: includes('loss', moneyLimits),
    realityCheck: not(isEmpty(getRealityCheckValues(state))),
    selfExclusion: not(isEmpty(getSelfExclusionValues(state))),
    timeOut: not(isEmpty(getTimeOutValues(state)))
  };
}
/**
 * Check if limit value exceeds the hard cap
 *
 * @memberof rushplay/compliance/jurisdiction
 * @param {Object} state Jurisdiction state
 * @param {Object} props
 * @param {string} props.id Money limit ID
 * @param {string} props.period Money limit period (ignored if ID is passed)
 * @param {string} props.type Money limit type (ignored if ID is passed)
 * @param {number} props.valueCents Current limit value in cents
 * @returns {boolean} `true` if value exceeds the hard cap; `false` otherwise
 */

function getMoneyLimitExcess(state, props) {
  var limit = getMoneyLimit(state, {
    id: props.id,
    period: props.period,
    type: props.type
  });
  var hardCap = pathOr(Infinity, ['hardCap'], limit);
  return props.valueCents > hardCap;
}

/** @module rushplay/compliance/jurisdiction */

export { fetch, save, clear, reducer, isSelfExclusionCrossbrand, isTimeOutCrossbrand, isBalanceTotalsVisible, getCurrentSessionTimeVisibility, getBriteSignUpAllowed, getBriteKycSignUpAllowed, getLicense, getLinks, getManualSignUpAllowed, getMoneyLimitTypes, getMoneyLimitPeriods, getMoneyLimits, getMoneyLimit, getSessionLimitTypes, getSessionLimitPeriods, getSessionLimits, getSessionLimit, getRealityCheckConfigurability, getRealityCheckRemovability, getRealityCheckTimerResetTrigger, getRealityCheckValues, getSelfExclusionValues, getTimeOutValues, getTrustlySignUpAllowed, getSettingsAvailability, getMoneyLimitExcess, SAVED, CLEARED };
